import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Breadcrumbs,
    Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    TextField,
    InputAdornment,
    Chip,
    Button,
    CircularProgress,
    Alert,
    Snackbar,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Helper function to get difficulty color
const getDifficultyColor = (difficulty) => {
    const map = {
        'EASY': '#4caf50',
        'easy': '#4caf50',
        'Easy': '#4caf50',
        'MEDIUM': '#ff9800',
        'medium': '#ff9800',
        'Medium': '#ff9800',
        'HARD': '#f44336',
        'hard': '#f44336',
        'Hard': '#f44336'
    };
    return map[difficulty] || '#9e9e9e';
};

const CompanyProblems = () => {
    const { companyId } = useParams();
    const navigate = useNavigate();

    const [company, setCompany] = useState(null);
    const [problems, setProblems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAlert, setShowAlert] = useState(false);

    // Table state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [orderBy, setOrderBy] = useState('frequency');
    const [order, setOrder] = useState('desc');
    const [searchQuery, setSearchQuery] = useState('');
    const [difficultyFilter, setDifficultyFilter] = useState('all');
    const [topicFilter, setTopicFilter] = useState('');
    const [topics, setTopics] = useState([]);

    // Fetch company details and problems
    useEffect(() => {
        const fetchCompanyData = async () => {
            try {
                setLoading(true);

                // Step 1: Fetch company details
                const companyResponse = await fetch(`${process.env.REACT_APP_BACK_END_URL}/coding-problems/companies/${companyId}`);

                if (!companyResponse.ok) {
                    throw new Error(`Error fetching company: ${companyResponse.status}`);
                }

                const companyData = await companyResponse.json();
                setCompany(companyData);

                // Step 2: Fetch problems with query parameters
                let problemsUrl = `${process.env.REACT_APP_BACK_END_URL}/coding-problems/problems/${companyData.name}`;

                const problemsResponse = await fetch(problemsUrl);

                if (!problemsResponse.ok) {
                    throw new Error(`Error fetching problems: ${problemsResponse.status}`);
                }

                const problemsData = await problemsResponse.json();
                setProblems(problemsData);

                // Extract unique topics from problems
                const allTopics = new Set();
                problemsData.forEach(problem => {
                    if (problem.topics) {
                        problem.topics.split(',').forEach(topic => {
                            allTopics.add(topic.trim());
                        });
                    }
                });
                setTopics(Array.from(allTopics).sort());

                setLoading(false);
            } catch (err) {
                console.error('Error fetching company data:', err);
                setError('Failed to load company problems. Please try again later.');
                setShowAlert(true);
                setLoading(false);
            }
        };

        if (companyId) {
            fetchCompanyData();
        }
    }, [companyId]);

    // Handle sort request
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // Handle page change
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // Handle rows per page change
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Handle alert close
    const handleAlertClose = () => {
        setShowAlert(false);
    };

    // Handle back button click
    const handleBackClick = () => {
        navigate(`/company/${companyId}`);
    };

    // Filter problems based on search, difficulty, and topic
    const getFilteredProblems = () => {
        return problems.filter(problem => {
            // Search filter
            const matchesSearch = !searchQuery ||
                problem.title.toLowerCase().includes(searchQuery.toLowerCase());

            // Difficulty filter
            const matchesDifficulty = difficultyFilter === 'all' ||
                problem.difficulty?.toLowerCase() === difficultyFilter.toLowerCase();

            // Topic filter
            const matchesTopic = !topicFilter ||
                (problem.topics && problem.topics.toLowerCase().includes(topicFilter.toLowerCase()));

            return matchesSearch && matchesDifficulty && matchesTopic;
        });
    };

    // Sort problems
    const getSortedProblems = () => {
        const filtered = getFilteredProblems();

        return filtered.sort((a, b) => {
            let valueA = a[orderBy];
            let valueB = b[orderBy];

            // Handle null values
            if (valueA === null) valueA = order === 'asc' ? Infinity : -Infinity;
            if (valueB === null) valueB = order === 'asc' ? Infinity : -Infinity;

            // Compare
            if (valueA < valueB) {
                return order === 'asc' ? -1 : 1;
            }
            if (valueA > valueB) {
                return order === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    // Get current page problems
    const getCurrentPageProblems = () => {
        const sorted = getSortedProblems();
        return sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg">
                <Box my={4}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBackClick}
                        sx={{ mb: 2 }}
                    >
                        Back to Tree View
                    </Button>

                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                </Box>
            </Container>
        );
    }

    if (!company) {
        return null;
    }

    const filteredProblems = getFilteredProblems();

    return (
        <Container maxWidth="lg">
            <Box my={4}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                    <Link
                        underline="hover"
                        color="inherit"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate('/companies')}
                    >
                        Companies
                    </Link>
                    <Link
                        underline="hover"
                        color="inherit"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/company/${companyId}`)}
                    >
                        {company.name}
                    </Link>
                    <Typography color="text.primary">Problems</Typography>
                </Breadcrumbs>

                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h4" component="h1">
                        {company.name} Problems
                    </Typography>

                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBackClick}
                    >
                        Back to Tree View
                    </Button>
                </Box>

                <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                    <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
                        <TextField
                            label="Search problems"
                            variant="outlined"
                            fullWidth
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <FormControl variant="outlined" sx={{ minWidth: 120 }}>
                            <InputLabel>Difficulty</InputLabel>
                            <Select
                                value={difficultyFilter}
                                onChange={(e) => setDifficultyFilter(e.target.value)}
                                label="Difficulty"
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="easy">Easy</MenuItem>
                                <MenuItem value="medium">Medium</MenuItem>
                                <MenuItem value="hard">Hard</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl variant="outlined" sx={{ minWidth: 200 }}>
                            <InputLabel>Topic</InputLabel>
                            <Select
                                value={topicFilter}
                                onChange={(e) => setTopicFilter(e.target.value)}
                                label="Topic"
                            >
                                <MenuItem value="">All Topics</MenuItem>
                                {topics.map((topic) => (
                                    <MenuItem key={topic} value={topic}>
                                        {topic}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <Box mt={2}>
                        <Typography variant="body2" color="text.secondary">
                            Showing {filteredProblems.length} of {problems.length} problems
                        </Typography>
                    </Box>
                </Paper>

                <TableContainer component={Paper} elevation={3}>
                    <Table sx={{ minWidth: 650 }} aria-label="problems table">
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'title'}
                                        direction={orderBy === 'title' ? order : 'asc'}
                                        onClick={() => handleRequestSort('title')}
                                    >
                                        Problem
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'difficulty'}
                                        direction={orderBy === 'difficulty' ? order : 'asc'}
                                        onClick={() => handleRequestSort('difficulty')}
                                    >
                                        Difficulty
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'frequency'}
                                        direction={orderBy === 'frequency' ? order : 'asc'}
                                        onClick={() => handleRequestSort('frequency')}
                                    >
                                        Frequency
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'acceptance_rate'}
                                        direction={orderBy === 'acceptance_rate' ? order : 'asc'}
                                        onClick={() => handleRequestSort('acceptance_rate')}
                                    >
                                        Acceptance Rate
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>Topics</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {getCurrentPageProblems().map((problem) => (
                                <TableRow key={problem.id} hover>
                                    <TableCell>
                                        <Link href={problem.link} target="_blank" rel="noopener noreferrer">
                                            {problem.title}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={problem.difficulty}
                                            size="small"
                                            sx={{
                                                bgcolor: getDifficultyColor(problem.difficulty),
                                                color: 'white',
                                                fontWeight: 'bold'
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {problem.frequency !== null
                                            ? `${problem.frequency.toFixed(1)}%`
                                            : 'N/A'}
                                    </TableCell>
                                    <TableCell>
                                        {problem.acceptance_rate !== null
                                            ? `${(problem.acceptance_rate * 100).toFixed(1)}%`
                                            : 'N/A'}
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {problem.topics ? problem.topics.split(',').map((topic, index) => (
                                                <Chip
                                                    key={index}
                                                    label={topic.trim()}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontSize: '0.7rem' }}
                                                    onClick={() => setTopicFilter(topic.trim())}
                                                />
                                            )) : 'N/A'}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={filteredProblems.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </TableContainer>
            </Box>

            <Snackbar open={showAlert} autoHideDuration={6000} onClose={handleAlertClose}>
                <Alert onClose={handleAlertClose} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default CompanyProblems;